import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationType } from '@/types/notification.types';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import clsx from 'clsx';
import {
  NotificationListQueryParams,
  INotificationData,
} from '@/types/notification.types';
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useDeleteAllNotificationsMutation,
  useDeleteNotificationsByIdsMutation,
  useMarkNotificationAsReadMutation,
} from '@/services/NotificationService';
import { enqueueSnackbar } from 'notistack';
import { getErrorMessage } from '@/utils/errorUtils';
import { NOTIFICATIONS_POLLING_INTERVAL } from '@/constant/notifications';
import { ConfirmationDialog } from '@/components/dialogBox';
import { formatDisplayDateTime } from '@/utils/Date';

const Notifications = () => {
  const navigate = useNavigate();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>(
    []
  );
  const [confirmDeleteAll, setConfirmDeleteAll] = useState<boolean>(false);

  const queryParams: NotificationListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
  };

  const {
    data: notificationsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetNotificationsQuery(queryParams, {
    pollingInterval: NOTIFICATIONS_POLLING_INTERVAL,
  });

  const [
    markAllNotificationsAsRead,
    { isSuccess: markAllSuccess, isError: markAllError },
  ] = useMarkAllNotificationsAsReadMutation();
  const [
    deleteAllNotifications,
    { isSuccess: deleteAllSuccess, isError: deleteAllError },
  ] = useDeleteAllNotificationsMutation();
  const [
    deleteNotificationsByIds,
    { isSuccess: deleteSelectedSuccess, isError: deleteSelectedError },
  ] = useDeleteNotificationsByIdsMutation();
  const [
    markNotificationAsRead,
    { isSuccess: markSingleSuccess, isError: markSingleError },
  ] = useMarkNotificationAsReadMutation();

  useEffect(() => {
    if (notificationsResponse) {
      handleTotalPages(notificationsResponse.total_pages || 1);
      handleTotalItems(notificationsResponse.total_items || 0);
    }
  }, [notificationsResponse, handleTotalPages, handleTotalItems]);

  // Explicitly refetch when query parameters change
  useEffect(() => {
    refetch();
  }, [debouncedValue, pagination.pageIndex, pagination.pageSize, refetch]);

  // Handle refetch and feedback for mark all as read
  useEffect(() => {
    if (markAllSuccess) {
      enqueueSnackbar('All notifications marked as read', {
        variant: 'success',
      });
      setSelectedNotifications([]);
      refetch();
    }
    if (markAllError) {
      enqueueSnackbar('Failed to mark all notifications as read', {
        variant: 'error',
      });
    }
  }, [markAllSuccess, markAllError, refetch]);

  // Handle refetch and feedback for delete all
  useEffect(() => {
    if (deleteAllSuccess) {
      enqueueSnackbar('All notifications deleted', { variant: 'success' });
      setConfirmDeleteAll(false);
      setSelectedNotifications([]);
      refetch();
    }
    if (deleteAllError) {
      enqueueSnackbar('Failed to delete all notifications', {
        variant: 'error',
      });
      setConfirmDeleteAll(false);
    }
  }, [deleteAllSuccess, deleteAllError, refetch]);

  // Handle refetch and feedback for delete selected
  useEffect(() => {
    if (deleteSelectedSuccess) {
      enqueueSnackbar('Selected notifications deleted', { variant: 'success' });
      setSelectedNotifications([]);
      refetch();
    }
    if (deleteSelectedError) {
      enqueueSnackbar('Failed to delete selected notifications', {
        variant: 'error',
      });
    }
  }, [deleteSelectedSuccess, deleteSelectedError, refetch]);

  // Handle refetch and feedback for mark single notification as read
  useEffect(() => {
    if (markSingleSuccess) {
      enqueueSnackbar('Notification marked as read', { variant: 'success' });
      refetch();
    }
    if (markSingleError) {
      enqueueSnackbar('Failed to mark notification as read', {
        variant: 'error',
      });
    }
  }, [markSingleSuccess, markSingleError, refetch]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
    setSelectedNotifications([]);
  };

  const handleBulkRead = async () => {
    try {
      await markAllNotificationsAsRead().unwrap();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Failed to mark all notifications as read'),
        { variant: 'error' }
      );
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteAllNotifications().unwrap();
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Failed to delete all notifications'),
        { variant: 'error' }
      );
      setConfirmDeleteAll(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      await deleteNotificationsByIds({
        notification_ids: selectedNotifications,
      }).unwrap();
    } catch (error) {
      console.error('Failed to delete selected notifications:', error);
      enqueueSnackbar(
        getErrorMessage(error, 'Failed to delete selected notifications'),
        { variant: 'error' }
      );
    }
  };

  const handleMarkAsRead = useCallback(
    async (notification: INotificationData) => {
      try {
        await markNotificationAsRead(notification.id).unwrap();
        if (notification.notification_type === NotificationType.BOOKING_CONFLICT_ALERT) {
          const bookingId =
            notification.metadata?.bookingId ||
            notification.metadata?.BOOKING_ID;
          const mediationCaseId =
            notification.metadata?.mediationCaseId ||
            notification.metadata?.MEDIATION_CASE_ID;
          if (bookingId) {
            const path = mediationCaseId
              ? `/booking-management/${bookingId}?mediationCaseId=${mediationCaseId}`
              : `/booking-management/${bookingId}`;
            navigate(path);
          }
        }
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        enqueueSnackbar(
          getErrorMessage(error, 'Failed to mark notification as read'),
          { variant: 'error' }
        );
      }
    },
    [markNotificationAsRead, navigate]
  );

  const renderDescriptionWithLinks = (description?: string) => {
    if (!description) return 'No description';

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = description.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        urlRegex.lastIndex = 0;
        return (
          <a
            key={index}
            href={part}
            target="_self"
            rel="noreferrer"
            className="text-primary contrast:text-blue-200 underline break-all"
          >
            {part}
          </a>
        );
      }
      urlRegex.lastIndex = 0;
      return <span key={index}>{part}</span>;
    });
  };

  const isLikelyHtml = (value?: string) => {
    if (!value) return false;
    return /<\/?[a-z][\s\S]*>/i.test(value);
  };

  const handleCheckboxChange = (notificationId: string) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: 'Select',
        cell: ({ row }: { row: { original: INotificationData } }) => (
          <input
            type="checkbox"
            id={`notification-select-${row.original.id}`}
            checked={selectedNotifications.includes(row.original.id)}
            onChange={() => handleCheckboxChange(row.original.id)}
            className="checkbox checkbox-sm cursor-pointer"
            aria-label={`Select notification ${row.original.id}`}
          />
        ),
        meta: {
          className: 'w-[50px]',
        },
        enableSorting: false,
      },
      {
        accessorFn: (row: INotificationData) => row.title,
        id: 'title',
        header: 'Title',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => {
          const title = row?.original?.title || 'No title';
          return (
            <span className="text-gray-800 font-normal whitespace-nowrap md:whitespace-normal">
              {title}
            </span>
          );
        },
        meta: {
          className: 'min-w-[280px] md:min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: INotificationData) => row.description,
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => {
          const desc = row?.original?.description;
          if (desc && isLikelyHtml(desc)) {
            return (
              <div
                className="text-gray-800 font-normal whitespace-nowrap md:whitespace-normal prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            );
          }
          return (
            <span className="text-gray-800 font-normal whitespace-nowrap md:whitespace-normal">
              {renderDescriptionWithLinks(desc)}
            </span>
          );
        },
        meta: {
          className: 'min-w-[520px] md:min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: INotificationData) => row.created_at,
        id: 'timestamp',
        header: 'Timestamp',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => {
          return row?.original?.created_at
            ? formatDisplayDateTime(row.original.created_at, {
                includeSeconds: true,
              })
            : 'No date';
        },
        meta: {
          className: 'min-w-0 sm:min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: INotificationData) => row.is_read,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => {
          return (
            <span
              className={clsx(
                'badge badge-sm',
                row?.original?.is_read !== undefined
                  ? row.original.is_read
                    ? 'badge-success'
                    : 'badge-warning'
                  : 'badge-light'
              )}
            >
              {row?.original?.is_read !== undefined
                ? row.original.is_read
                  ? 'Read'
                  : 'Unread'
                : 'Unknown'}
            </span>
          );
        },
        meta: {
          className: 'min-w-0 sm:min-w-[150px]',
        },
      },
      {
        accessorFn: (row: INotificationData) => row?.metadata?.harborName,
        id: 'harborName',
        header: 'Harbor',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => {
          return (
            <span className="text-gray-800 font-normal break-all md:break-normal">
              {row?.original?.metadata?.harborName || 'N/A'}
            </span>
          );
        },
        meta: {
          className: 'min-w-0 sm:min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: INotificationData } }) => (
          <div className="flex items-center gap-2">
            {row.original.is_read ? (
              <span className="badge badge-sm badge-light text-gray-600">
                Read
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handleMarkAsRead(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Mark as Read"
                aria-label={`Mark ${row.original.title} as read`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[80px]' },
      },
    ],
    [selectedNotifications, handleMarkAsRead]
  );

  return (
    <div className="notifications-listings card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Search Input */}
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-notifications"
              aria-label="Search notifications"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-notifications"
                placeholder="Search notifications"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search notifications"
              />
            </label>
          </div>

          {/* Bulk Actions */}
          <button
            className="btn btn-sm btn-primary flex items-center gap-2 h-8 sm:h-10"
            onClick={handleBulkRead}
            aria-label="Mark all notifications as read"
            title="Mark all as read"
          >
            <KeenIcon
              type="solid"
              icon="check"
              className="text-white"
              aria-hidden="true"
            />
            Mark All As Read
          </button>
          <button
            className="btn btn-sm btn-danger flex items-center gap-2 h-8 sm:h-10"
            onClick={() => setConfirmDeleteAll(true)}
            aria-label="Delete all notifications"
            title="Delete all notifications"
          >
            <KeenIcon
              type="solid"
              icon="trash"
              className="text-white"
              aria-hidden="true"
            />
            Delete All
          </button>
          {selectedNotifications.length > 0 && (
            <button
              className="btn btn-sm btn-danger flex items-center gap-2 h-8 sm:h-10"
              onClick={handleDeleteSelected}
              aria-label="Delete selected notifications"
              title="Delete selected notifications"
            >
              <KeenIcon type="solid" icon="trash" className="text-white" />
              Delete Selected
            </button>
          )}
        </div>

        {/* Clear Filters Button */}
        {debouncedValue?.trim() && (
          <div className="flex items-center">
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Search
            </button>
          </div>
        )}
      </div>
      <div className="card-body overflow-x-hidden sm:overflow-x-visible">
        {isLoading || isFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading notifications...
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            Failed to load notifications. Please try again.
          </div>
        ) : (
          <div className="notifications-grid-wrapper">
            <DataGrid
              columns={columns}
              data={notificationsResponse?.data || []}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: totalItems,
                totalPages: totalPages,
                setPagination,
                info: '{from} - {to} of {count}',
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'timestamp', desc: true }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={searchTerm !== ''}
            />
          </div>
        )}
        <ConfirmationDialog
          data-testid="confirmation-dialog-delete-all"
          open={confirmDeleteAll}
          onClose={() => setConfirmDeleteAll(false)}
          onConfirm={handleBulkDelete}
          message="Are you sure you want to delete all notifications?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-all-confirmation-title"
          aria-describedby="delete-all-confirmation-description"
        />
      </div>
    </div>
  );
};

export { Notifications };
